import { gql, useMutation, useQuery } from '@apollo/client';
import { SWITCH_MANDATE_TEXT_DRAFT, SWITCH_MANDATE_TEXT_VERSION } from '@orn/global/billing/mandateText.js';
import { ApiErrorCode } from '@orn/global/errors/apiErrorCodes.js';
import Button from '@orn/shared-web/components/Button/Button';
import * as React from 'react';

import { extractErrorCode } from '../../utils/apollo/error';
import PlaidConnectionModal from '../plaid/PlaidConnectionModal';
import { useAccountLink } from '../plaid/useAccountLink';

import { switchAccountErrorMessage } from './checkoutErrors';
import MandateAuthorizationBase from './MandateAuthorization/MandateAuthorizationBase';
import PaymentAccountPickerBase from './PaymentAccountPicker/PaymentAccountPickerBase';
import { ELIGIBLE_BILLING_ACCOUNT_FRAGMENT, useAccountPicker } from './PaymentAccountPicker/useAccountPicker';

import type {
  SwitchAccountDataQuery,
  SwitchAccountDataQueryVariables,
  SwitchPaymentAccountMutation,
  SwitchPaymentAccountMutationVariables,
} from './__generated__/SwitchAccountFlow.gql';

const SWITCH_ACCOUNT_DATA = gql`
  query SwitchAccountData {
    eligibleBillingAccounts {
      ...EligibleBillingAccount
    }
    currentSubscription {
      switchTerms {
        amountDisplay
        firstChargeDisplay
      }
      paymentMethod {
        institutionName
        mask
      }
    }
  }
  ${ELIGIBLE_BILLING_ACCOUNT_FRAGMENT}
`;

const SWITCH_PAYMENT_ACCOUNT = gql`
  mutation SwitchPaymentAccount($input: SwitchPaymentAccountInput!) {
    switchPaymentAccount(input: $input) {
      institutionName
      mask
    }
  }
`;

export interface ISwitchAccountFlowProps {
  /** Called once the membership debits the new account. */
  onComplete: () => void;
  /** Leaves the flow without switching. */
  onExit: () => void;
}

/**
 * The two-screen payment-account switch: pick a linked account, then
 * authorize a fresh debit mandate against it. Reuses checkout's picker and
 * authorization screens — this is the bank half of payment-method management,
 * living in-app because the Stripe portal cannot list Plaid-linked accounts.
 *
 * The acknowledgment is answered by resubmitting, the same shape as checkout:
 * the server's refusal is the only reliable signal that the disclosure is
 * needed, and it costs nothing beyond the owner-name check.
 */
const SwitchAccountFlow = ({ onComplete, onExit }: ISwitchAccountFlowProps) => {
  const { data, loading, error, refetch } = useQuery<SwitchAccountDataQuery, SwitchAccountDataQueryVariables>(
    SWITCH_ACCOUNT_DATA
  );
  const accountLink = useAccountLink({ onLinked: () => void refetch() });
  const [switchPaymentAccount, { loading: isSubmitting }] = useMutation<
    SwitchPaymentAccountMutation,
    SwitchPaymentAccountMutationVariables
  >(SWITCH_PAYMENT_ACCOUNT);

  // No pre-selection, deliberately: the whole point is choosing an account
  // other than the one already debiting, and this screen cannot tell which
  // eligible account that is.
  const [screen, setScreen] = React.useState<'account' | 'authorize'>('account');
  const [selectedAccountId, setSelectedAccountId] = React.useState<string | null>(null);
  const [thirdPartyAck, setThirdPartyAck] = React.useState(false);
  const [requiresThirdPartyAck, setRequiresThirdPartyAck] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);

  const { accounts, selectedAccount, selectAccount } = useAccountPicker({
    eligibleBillingAccounts: data?.eligibleBillingAccounts,
    loading,
    selectedAccountId,
    onSelect: setSelectedAccountId,
    onAccountChanged: () => {
      setThirdPartyAck(false);
      setRequiresThirdPartyAck(false);
      setErrorMessage(undefined);
    },
    onSelectionInvalidated: () => {
      setSelectedAccountId(null);
      setScreen('account');
    },
  });

  const handleSubmit = async (displayedTerms: { amountDisplay: string; firstChargeDisplay: string }) => {
    if (selectedAccountId === null) {
      return;
    }
    setErrorMessage(undefined);
    let result: Awaited<ReturnType<typeof switchPaymentAccount>>;
    try {
      result = await switchPaymentAccount({
        variables: {
          input: {
            financialAccountId: selectedAccountId,
            mandateTextVersion: SWITCH_MANDATE_TEXT_VERSION,
            thirdPartyAck,
            // Echoed verbatim: the server refuses when these no longer match
            // the live subscription, so the mandate evidence only ever
            // records the terms this screen actually showed.
            displayedAmount: displayedTerms.amountDisplay,
            displayedFirstCharge: displayedTerms.firstChargeDisplay,
          },
        },
        errorPolicy: 'all',
      });
    } catch {
      // errorPolicy covers GraphQL errors only; a transport failure rejects.
      setErrorMessage(switchAccountErrorMessage(ApiErrorCode.INTERNAL_SERVER_ERROR) ?? undefined);
      return;
    }

    if (result.data?.switchPaymentAccount != null) {
      onComplete();
      return;
    }

    const code = extractErrorCode(result.errors ?? []);
    if (code === ApiErrorCode.SUBSCRIBE_THIRD_PARTY_ACK_REQUIRED) {
      setRequiresThirdPartyAck(true);
      return;
    }
    if (code === ApiErrorCode.SWITCH_PAYMENT_TERMS_CHANGED) {
      // switchTerms reads Stripe live, so this refetch shows the terms a
      // resubmit would actually store.
      void refetch();
    }
    setErrorMessage(switchAccountErrorMessage(code) ?? undefined);
  };

  if (error != null && data == null) {
    return (
      <div className="flex w-full flex-col items-center gap-4">
        <p className="text-tertiary text-sm">We couldn't load your accounts. Check your connection and try again.</p>
        <Button type="button" size="md" color="secondary" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  if (loading || data == null) {
    return null;
  }

  // The authorization must state the exact terms the evidence row will store.
  // switchTerms is read live from Stripe, so a refetch after a terms-changed
  // refusal shows what a resubmit would actually record — not the webhook
  // mirror, which can lag a portal plan change.
  const terms = data.currentSubscription?.switchTerms ?? null;

  let content: React.ReactNode;
  if (terms === null) {
    content = (
      <div className="flex w-full flex-col items-center gap-4">
        <p className="text-tertiary text-sm">
          We can't switch your payment account right now. Please try again later or contact support.
        </p>
        <Button type="button" size="md" color="secondary" onClick={onExit}>
          Back to billing
        </Button>
      </div>
    );
  } else if (screen === 'account') {
    const current = data.currentSubscription?.paymentMethod ?? null;
    content = (
      <div className="flex w-full flex-col gap-4">
        {current !== null && (
          // The screen cannot pre-select the debiting account, but it can
          // name it, steering the member away from the same-account refusal.
          <p className="text-tertiary text-sm">
            {'Currently paying from '}
            {current.institutionName ?? 'Bank account'}
            {current.mask != null && ` ••${current.mask}`}
          </p>
        )}
        <PaymentAccountPickerBase
          accounts={accounts}
          selectedAccountId={selectedAccountId}
          onSelectAccount={selectAccount}
          onContinue={() => setScreen('authorize')}
          onLinkAnotherBank={accountLink.trigger}
          onCancel={onExit}
        />
      </div>
    );
  } else if (selectedAccount === null) {
    content = null;
  } else {
    content = (
      <MandateAuthorizationBase
        institution={selectedAccount.bankName}
        mask={selectedAccount.mask}
        amountDisplay={terms.amountDisplay}
        firstChargeDisplay={terms.firstChargeDisplay}
        requiresThirdPartyAck={requiresThirdPartyAck}
        thirdPartyAck={thirdPartyAck}
        onThirdPartyAckChange={setThirdPartyAck}
        onSubmit={() => void handleSubmit(terms)}
        onBack={() => setScreen('account')}
        isSubmitting={isSubmitting}
        errorMessage={errorMessage}
        submitLabel="Switch payment account"
        mandateTemplate={SWITCH_MANDATE_TEXT_DRAFT}
        chargeSummary={`Nothing is charged today. ${terms.amountDisplay} starting ${terms.firstChargeDisplay}.`}
      />
    );
  }

  return (
    <>
      {content}
      <PlaidConnectionModal
        isOpen={accountLink.isModalOpen}
        onOpenChange={accountLink.onModalOpenChange}
        onSelect={accountLink.onModalSelect}
        errorCode={accountLink.errorCode}
      />
    </>
  );
};

SwitchAccountFlow.displayName = 'SwitchAccountFlow';

export default SwitchAccountFlow;
